import React, { useState } from 'react';
import './EMICalculator.css';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(2500000);
  const [tenure, setTenure] = useState(8);
  const [interestRate, setInterestRate] = useState(10.5);

  const r = interestRate / 100 / 12;
  const n = tenure * 12;
  const emi = (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const totalPayable = emi * n;
  const interestPayable = totalPayable - loanAmount;

  // We use a clean Doughnut chart with a cutout for a modern, dashboard-like feel
  const chartData = {
    labels: ['Principal Amount', 'Interest Payable'],
    datasets: [
      {
        data: [loanAmount, interestPayable],
        backgroundColor: ['#2563eb', '#cbd5e1'], // Sapphire and Sleek Grey
        hoverBackgroundColor: ['#1d4ed8', '#94a3b8'],
        borderWidth: 0,
        weight: 1,
      },
    ],
  };

  const chartOptions = {
    cutout: '75%',
    plugins: {
      legend: {
        display: false, // Custom legend elements are built inside the UI below
      }
    }
  };

  // Helper for dynamic range backgrounds (fill track effect)
  const getBackgroundSize = (value, min, max) => {
    return { backgroundSize: `${((value - min) * 100) / (max - min)}% 100%` };
  };

  return (
    <div className="premium-emi-wrapper">
      <div className="emi-header">
        <span className="emi-eyebrow">Financial Suite</span>
        <h2 className="emi-heading">Calculate Your Car Loan</h2>
        <div className="emi-accent-line"></div>
      </div>

      <div className="premium-emi-container">
        <div className="emi-inputs-panel">
          
          {/* SLIDER 1: LOAN AMOUNT */}
          <div className="slider-group">
            <div className="slider-header">
              <label>Loan Amount</label>
              <div className="input-number-wrapper">
                <span className="currency-prefix">₹</span>
                <input
                  type="number"
                  min="50000"
                  max="5000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="range-container">
              <input
                type="range"
                min="50000"
                max="5000000"
                step="50000"
                value={loanAmount}
                style={getBackgroundSize(loanAmount, 50000, 5000000)}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
              />
              <div className="range-limits">
                <span>₹50K</span>
                <span>₹50L</span>
              </div>
            </div>
          </div>

          {/* SLIDER 2: TENURE */}
          <div className="slider-group">
            <div className="slider-header">
              <label>Loan Tenure (Years)</label>
              <div className="input-number-wrapper">
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                />
                <span className="currency-suffix">Yrs</span>
              </div>
            </div>
            <div className="range-container">
              <input
                type="range"
                min="1"
                max="30"
                value={tenure}
                style={getBackgroundSize(tenure, 1, 30)}
                onChange={(e) => setTenure(Number(e.target.value))}
              />
              <div className="range-limits">
                <span>1 Yr</span>
                <span>30 Yrs</span>
              </div>
            </div>
          </div>

          {/* SLIDER 3: INTEREST RATE */}
          <div className="slider-group">
            <div className="slider-header">
              <label>Interest Rate</label>
              <div className="input-number-wrapper">
                <input
                  type="number"
                  min="1"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                />
                <span className="currency-suffix">%</span>
              </div>
            </div>
            <div className="range-container">
              <input
                type="range"
                min="1"
                max="15"
                step="0.1"
                value={interestRate}
                style={getBackgroundSize(interestRate, 1, 15)}
                onChange={(e) => setInterestRate(Number(e.target.value))}
              />
              <div className="range-limits">
                <span>1 %</span>
                <span>15 %</span>
              </div>
            </div>
          </div>

        </div>

        {/* VISUAL BREAKDOWN PANEL */}
        <div className="emi-breakdown-panel">
          <div className="chart-wrapper">
            <Doughnut data={chartData} options={chartOptions} />
            <div className="chart-center-content">
              <span className="emi-monthly-label">Monthly EMI</span>
              <span className="emi-monthly-value">₹ {Math.round(emi).toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div className="kpi-grid">
            <div className="kpi-card principal">
              <span className="kpi-dot"></span>
              <span className="kpi-label">Principal Amount</span>
              <span className="kpi-value">₹ {loanAmount.toLocaleString('en-IN')}</span>
            </div>
            <div className="kpi-card interest">
              <span className="kpi-dot"></span>
              <span className="kpi-label">Total Interest</span>
              <span className="kpi-value">₹ {Math.round(interestPayable).toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div className="total-payable-strip">
            <span>Total Payable Amount</span>
            <strong>₹ {Math.round(totalPayable).toLocaleString('en-IN')}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMICalculator;